// Collapsible section headers with per-device persistence. Each collapsible remembers its open/closed
// state in localStorage keyed by a stable id, so it survives a hard refresh. Default: expanded.
// The visual (rotating caret + hidden body) is driven by the .js-collapse-head / .is-collapsed-body
// classes in app.css, so callers only wire behavior here.

const COLLAPSE_PREFIX = 'studio.collapse.';

/** Read a section's collapsed state (default expanded). Falls back to the default if storage is blocked. */
export function readCollapsed(key: string, def = false): boolean {
  try {
    const v = localStorage.getItem(COLLAPSE_PREFIX + key);
    if (v === 'open') return false;
    if (v === 'collapsed') return true;
  } catch { /* storage blocked (private mode) → use default */ }
  return def;
}

function writeCollapsed(key: string, collapsed: boolean): void {
  try { localStorage.setItem(COLLAPSE_PREFIX + key, collapsed ? 'collapsed' : 'open'); } catch { /* ignore */ }
}

/** Wire a header→body collapsible: the header toggles the body and a rotating caret, persisting state.
 *  `defaultCollapsed` applies only until this device has toggled the section — a stored choice always
 *  wins, so changing a default never overrides what the user set. */
export function wireCollapse(head: HTMLElement, body: HTMLElement, key: string, defaultCollapsed = false): void {
  head.classList.add('js-collapse-head');
  head.setAttribute('role', 'button');
  head.setAttribute('tabindex', '0');
  const apply = (collapsed: boolean): void => {
    head.classList.toggle('is-collapsed-head', collapsed);
    body.classList.toggle('is-collapsed-body', collapsed);
    head.setAttribute('aria-expanded', String(!collapsed));
  };
  apply(readCollapsed(key, defaultCollapsed));
  const toggle = (): void => {
    const collapsed = !head.classList.contains('is-collapsed-head');
    writeCollapsed(key, collapsed);
    apply(collapsed);
  };
  head.addEventListener('click', toggle);
  head.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
  });
}
